import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolDeltaConnData} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "../../../../common/types";
import {BiomarkerNames} from "../../types/biomarkerNames";
import {Affix, Box, Loader, Text, Select} from "@mantine/core";

interface DeltaConnectivityChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const DeltaConnectivityChart: React.FC<DeltaConnectivityChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [deltaConnData, setDeltaConnData] = useState<PlotParams>()
    const [loading, setLoading] = useState<boolean>(false)
    const [frequencyBand, setFrequencyBand] = useState<string>("beta")
    const [connectivityMethod, setConnectivityMethod] = useState<string>(BiomarkerNames.CONNECTIVITY_WPLI)

    const freqOptions = [
        { value: "delta", label: "Delta" },
        { value: "theta", label: "Theta" },
        { value: "alpha", label: "Alpha" },
        { value: "beta", label: "Beta" },
        { value: "gamma", label: "Gamma" },
        { value: "all", label: "All" },
    ]

    const connectivityMethodOptions = [
        { value: BiomarkerNames.CONNECTIVITY_WPLI, label: "wPLI" },
        { value: BiomarkerNames.CONNECTIVITY_TPLI, label: "tPLI" },
    ]

    useEffect(() => {
        if (selectedProtocol) {
            setLoading(true)
            loadProtocolDeltaConnData(activeCompany.id, selectedProtocol, connectivityMethod, ["pre_to_post_delta"], [frequencyBand], ["all"]).then((data: any) => {
                // data.data expected to be array of [stim_condition, stim_start_time, delta_connectivity]
                // build mapping
                const conn_map = new Map<string, number>();
                const stimSet = new Set<string>();
                (data.data || []).forEach(([stim, delta_conn]: [string, number]) => {
                    stimSet.add(stim);
                    conn_map.set(stim, delta_conn);
                });

                // Preserve order as sorted by stim_start_time in query
                const stimOrder = Array.from(stimSet);

                const lineTemplate = (data.figure?.data && (data.figure.data[0] as any)) || {} as any;
                const templateMarker = (lineTemplate && lineTemplate.marker) || {};
                const baseColor = templateMarker.color ?? templateMarker.fillcolor ?? "rgba(31,119,180,0.8)";

                const connTrace: any = {
                    ...lineTemplate,
                    x: stimOrder,
                    y: stimOrder.map((st) => conn_map.get(st) || 0),
                    marker: {
                        ...templateMarker,
                        color: baseColor,
                    },
                    mode: lineTemplate.mode || 'lines+markers',
                    hovertemplate: "%{x}: %{y}<extra></extra>",
                };

                setDeltaConnData({
                    layout: {
                        ...data.figure.layout,
                    },
                    config: { responsive: true },
                    data: [connTrace],
                })
            }).catch(() => {
                // eat the error since notification would already be called
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [selectedProtocol, refreshTrigger, frequencyBand, connectivityMethod])

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Frequency band</Text>
                    <Select
                        data={freqOptions}
                        value={frequencyBand}
                        onChange={(v) => setFrequencyBand(v || "all")}
                        style={{width: 220}}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Connectivity method</Text>
                    <Select
                        data={connectivityMethodOptions}
                        value={connectivityMethod}
                        onChange={(v) => setConnectivityMethod(v || BiomarkerNames.CONNECTIVITY_WPLI)}
                        style={{width: 220}}
                    />
                </Box>
            </Box>

            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {deltaConnData ? (
                    <Box style={{height: '100%', width: '100%'}}>
                        <PlotlyPlot {...deltaConnData}/>
                        {loading ? renderLoading() : null}
                    </Box>
                ) : (
                    <Box style={{height: '100%', position: 'relative'}}>
                        {renderLoading()}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
